/**
 * @module pages/PledgePage
 * @description Digital pledge card (card back). Left panel carries the
 * edition's message and progress panel; right panel is the form. After
 * submit, donors who chose to give online are handed to AFJ's Neon CRM
 * payment form.
 */
import { useState } from "react";
import { PledgeForm } from "../components/PledgeForm";
import { ChallengePanel } from "../components/MatchMeter";
import { BrandLockup } from "../components/Brand";
import { ABOUT_YOUR_GIFT, CAMPAIGN, ONLINE_PAYMENT_INTENTS, formatUsd } from "../config/campaign";
import { hrefFor } from "../app/router";
import { useEdition } from "../app/edition";
import type { Pledge } from "../domain/types";

export function PledgePage({ fromSlug }: { fromSlug: string | null }) {
    const [submitted, setSubmitted] = useState<Pledge | null>(null);
    const edition = useEdition();
    const { pledge: copy } = edition;

    return (
        <>
            <section className="pledge-page">
                <aside className="pledge-aside">
                    <BrandLockup variant="vertical" className="pledge-mark" />
                    <p className="pledge-to">To Jamaica,</p>
                    <p className="pc-script pledge-script">With Love.</p>
                    <hr className="pc-rule pc-rule--short" />
                    <p className={edition.showMatch ? "pledge-cap" : "pledge-cap pledge-cap--words"}>{copy.asideHeadline}</p>
                    {copy.asideKicker && <p className="pc-kicker">{copy.asideKicker}</p>}
                    <p className="pledge-lede">{copy.asideLede}</p>
                    <p className="pc-tagline">{copy.asideTagline}</p>
                    <ChallengePanel variant="compact" />
                </aside>

                <div className="pledge-main">
                    {submitted ? (
                        <div className="thanks" role="status">
                            <p className="pc-kicker">Thank you{submitted.anonymous ? "" : `, ${submitted.name.split(" ")[0]}`}</p>
                            <h1 className="section-title">{copy.thanksHeadline(submitted.amount)}</h1>
                            <p>
                                {/* TODO: Receipt email arrives in phase 2 (Neon CRM sends tax receipts on payment).
                                    Caused by: no mail/CRM credentials in the demo. Chosen: say only what is true now.
                                    Side-effect: none — copy changes once the Neon integration lands. */}
                                Your {formatUsd(submitted.amount)} pledge is recorded under <strong>{submitted.email}</strong>.
                                {submitted.frequency === "monthly" && " Thank you for giving all year long."}
                            </p>
                            {ONLINE_PAYMENT_INTENTS.has(submitted.paymentIntent) ? (
                                <a className="btn btn--primary btn--lg" href={CAMPAIGN.neonDonationUrl} target="_blank" rel="noopener noreferrer">
                                    Complete my gift securely →
                                </a>
                            ) : submitted.paymentIntent === "mail-check" ? (
                                <p>
                                    Please make your check payable to <strong>{CAMPAIGN.organizationName}, Inc.</strong> and mail it to{" "}
                                    {CAMPAIGN.mailingAddress}.
                                </p>
                            ) : (
                                <p>An AFJ team member will follow up to arrange your gift.</p>
                            )}
                            <p className="pf-fine">Secure payment is processed by AFJ's donation platform (Neon CRM).</p>
                            <a className="btn" href={hrefFor({ name: "home" })}>See more stories</a>
                        </div>
                    ) : (
                        <PledgeForm key={edition.id} sourceCardSlug={fromSlug} onSubmitted={setSubmitted} />
                    )}
                </div>
            </section>
            {copy.showAboutYourGift && (
                <section className="about-gift" aria-labelledby="about-gift-h">
                    <h2 id="about-gift-h">About your gift</h2>
                    <p>{ABOUT_YOUR_GIFT}</p>
                </section>
            )}
        </>
    );
}
